import React, { useState, useEffect } from "react";
import {
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainContainer: { padding: theme.spacing(3) },
  filterPaper: { padding: theme.spacing(2), marginBottom: theme.spacing(3) },
  tableRow: { "&:hover": { backgroundColor: theme.palette.action.hover } },
  badgePendente: { backgroundColor: "#fff3e0", color: "#e65100", fontWeight: "bold" },
  badgeAndamento: { backgroundColor: "#e3f2fd", color: "#0d47a1", fontWeight: "bold" },
  badgeConcluido: { backgroundColor: "#e8f5e9", color: "#1b5e20", fontWeight: "bold" },
  badgeCancelado: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: "bold" },
  processBox: {
    backgroundColor: "#fafafa",
    padding: theme.spacing(2),
    borderRadius: 4,
    border: "1px solid #e0e0e0",
    marginTop: theme.spacing(1),
    maxHeight: 250,
    overflowY: "auto"
  },
  processRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(1) },
  checklistContainer: {
    maxHeight: 300,
    overflowY: "auto",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: theme.spacing(2),
    backgroundColor: "#fdfdfd",
    marginTop: theme.spacing(1)
  },
  actionButtons: { display: "flex", gap: theme.spacing(1) }
}));

export default function TicketsCustom() {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [estruturaServicos, setEstruturaServicos] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estados para busca dinâmica de contatos
  const [contactsOptions, setContactsOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadedContactsCache, setLoadedContactsCache] = useState([]);

  // 1. CARREGAR DADOS DO BACKEND
  const fetchEstrutura = async () => {
    try {
      const { data } = await api.get("/custom-servicos");
      setEstruturaServicos(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Falta criar a rota /custom-servicos no backend.");
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/tickets-custom");
      const ticketsData = Array.isArray(data) ? data : [];
      setTickets(ticketsData);

      // Busca inicial para popular o cache com os contatos mais recentes
      const { data: contactsData } = await api.get("/contacts", {
        params: { searchParam: "", pageNumber: 1, limit: 100 }
      });
      const fetched = contactsData.contacts || contactsData || [];
      setLoadedContactsCache(Array.isArray(fetched) ? fetched : []);
    } catch (err) {
      console.error(err);
      toast.error("Falta criar a rota GET /tickets-custom no backend.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      if (data && data.users) {
        setUsers(data.users);
      } else if (Array.isArray(data)) {
        setUsers(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchEstrutura();
    fetchTickets();
  }, []);

  // Debounce para busca dinâmica de contatos
  useEffect(() => {
    if (!searchParam || searchParam.length < 3) {
      setContactsOptions([]);
      setLoadingContacts(false);
      return;
    }

    setLoadingContacts(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactsDynamic = async () => {
        try {
          const { data } = await api.get("/contacts", {
            params: { searchParam },
          });
          const fetchedContacts = data.contacts || data || [];
          setContactsOptions(Array.isArray(fetchedContacts) ? fetchedContacts : []);

          setLoadedContactsCache(prev => {
            const merged = [...prev];
            if (Array.isArray(fetchedContacts)) {
              fetchedContacts.forEach(c => {
                if (!merged.find(m => String(m.id) === String(c.id))) merged.push(c);
              });
            }
            return merged;
          });

          setLoadingContacts(false);
        } catch (err) {
          setLoadingContacts(false);
          console.error(err);
        }
      };

      fetchContactsDynamic();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openOperacionalModal, setOpenOperacionalModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [feedbackActionType, setFeedbackActionType] = useState("");
  const [feedbackText, setFeedbackText] = useState("");

  const [filterId, setFilterId] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const [filterDescricao, setFilterDescricao] = useState("");
  const [filterAnalista, setFilterAnalista] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [formTicket, setFormTicket] = useState({
    id: null,
    nome: "",
    contactId: "",
    descricao: "",
    userId: "",
    status: "pendente",
    servicoId: "",
    sistemaId: "",
    processosQuantitativos: {},
    bonificacao: 0
  });

  const [sistemasDisponiveis, setSistemasDisponiveis] = useState([]);
  const [processosDisponiveis, setProcessosDisponiveis] = useState([]);

  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setSistemasDisponiveis([]);
    setProcessosDisponiveis([]);
    setSearchParam("");
    setFormTicket({
      id: null,
      nome: "",
      contactId: "",
      descricao: "",
      userId: "",
      status: "pendente",
      servicoId: "",
      sistemaId: "",
      processosQuantitativos: {},
      bonificacao: 0
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (ticket) => {
    setIsEditing(true);

    const servico = estruturaServicos.find(s => Number(s.id) === Number(ticket.servicoId));
    const sistemas = servico ? servico.sistemas : [];
    setSistemasDisponiveis(sistemas);

    const sistema = sistemas.find(sys => Number(sys.id) === Number(ticket.sistemaId));
    const processos = sistema ? sistema.processos : [];
    setProcessosDisponiveis(processos);

    const quantitativos = {};
    processos.forEach(p => {
      const tarefasDoProcesso = ticket.tarefas ? ticket.tarefas.filter(t => Number(t.processoId) === Number(p.id)) : [];
      const valorUnitarioEditado = tarefasDoProcesso.length > 0 ? tarefasDoProcesso[0].valorUnitario : p.valorUnitario;
      quantitativos[p.id] = {
        qtd: tarefasDoProcesso.length,
        valorEditado: valorUnitarioEditado
      };
    });

    // const currentContact = loadedContactsCache.find(c => String(c.id) === String(ticket.contactId)) || ticket.contact;
    // if (currentContact) {
    //   setContactsOptions([currentContact]);
    // }

    // Garantir que se ticket.contact existir, ele seja inserido no cache e nas opções
    if (ticket.contact) {
      setContactsOptions([ticket.contact]);
      setLoadedContactsCache(prev => {
        if (!prev.some(c => String(c.id) === String(ticket.contact.id))) {
          return [...prev, ticket.contact];
        }
        return prev;
      });
    } else if (ticket.contactId) {
      // Caso o backend só traga o ID sem o objeto de contato embutido, buscar via API
      api.get(`/contacts/${ticket.contactId}`).then(({ data }) => {
        if (data) {
          setContactsOptions([data]);
          setLoadedContactsCache(prev => [...prev, data]);
        }
      }).catch(() => { });
    }

    setSearchParam("");
    setFormTicket({
      id: ticket.id,
      nome: ticket.nome || "",
      contactId: ticket.contactId || "",
      descricao: ticket.descricao || "",
      userId: ticket.userId || "",
      status: ticket.status || "pendente",
      servicoId: ticket.servicoId || "",
      sistemaId: ticket.sistemaId || "",
      processosQuantitativos: quantitativos,
      bonificacao: ticket.bonificacao || 0,
      tarefas: ticket.tarefas || []
    });

    setOpenModal(true);
  };

  const handleServicoChange = (servicoId) => {
    const servico = estruturaServicos.find(s => Number(s.id) === Number(servicoId));
    setSistemasDisponiveis(servico ? servico.sistemas : []);
    setProcessosDisponiveis([]);
    setFormTicket(prev => ({ ...prev, servicoId, sistemaId: "", processosQuantitativos: {}, bonificacao: 0 }));
  };

  const handleSistemaChange = (sistemaId) => {
    const sistema = sistemasDisponiveis.find(s => Number(s.id) === Number(sistemaId));
    if (sistema) {
      setProcessosDisponiveis(sistema.processos);
      const inicial = {};
      sistema.processos.forEach(p => {
        inicial[p.id] = { qtd: 0, valorEditado: p.valorUnitario };
      });
      setFormTicket(prev => ({ ...prev, sistemaId, processosQuantitativos: inicial, bonificacao: 0 }));
    } else {
      setProcessosDisponiveis([]);
      setFormTicket(prev => ({ ...prev, sistemaId: "", processosQuantitativos: {}, bonificacao: 0 }));
    }
  };

  const handleProcessoValoresChange = (procId, campo, valor) => {
    const novoValor = campo === "qtd" ? Math.max(0, parseInt(valor) || 0) : Math.max(0, parseFloat(valor) || 0);

    const novasConfiguracoes = {
      ...formTicket.processosQuantitativos,
      [procId]: {
        ...formTicket.processosQuantitativos[procId],
        [campo]: novoValor
      }
    };

    const total = Object.keys(novasConfiguracoes).reduce((soma, key) => {
      const item = novasConfiguracoes[key];
      return soma + ((item.qtd || 0) * (item.valorEditado || 0));
    }, 0);

    setFormTicket(prev => ({ ...prev, processosQuantitativos: novasConfiguracoes, bonificacao: total }));
  };

  const gerarTarefasUnicas = (processosQuantitativos, procDisponiveis) => {
    const tarefas = [];
    let count = 1;

    Object.keys(processosQuantitativos).forEach(procId => {
      const config = processosQuantitativos[procId];
      const processoOriginal = procDisponiveis.find(p => Number(p.id) === Number(procId));

      if (processoOriginal && config.qtd > 0) {
        for (let i = 1; i <= config.qtd; i++) {
          tarefas.push({
            uid: `task_${Date.now()}_${count++}`,
            processoId: Number(procId),
            nome: `${processoOriginal.nome} #${i}`,
            valorUnitario: config.valorEditado,
            concluida: false
          });
        }
      }
    });
    return tarefas;
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();

    try {
      if (isEditing) {
        const tarefasAtuais = [...(formTicket.tarefas || [])];
        const novasTarefasMapeadas = [];
        let count = 1;

        Object.keys(formTicket.processosQuantitativos).forEach(procId => {
          const config = formTicket.processosQuantitativos[procId];
          const procOriginal = processosDisponiveis.find(p => Number(p.id) === Number(procId));
          if (procOriginal) {
            const concluidasExistentes = tarefasAtuais.filter(t => Number(t.processoId) === Number(procId) && t.concluida);
            for (let i = 1; i <= config.qtd; i++) {
              const tarefaExistente = concluidasExistentes[i - 1];
              novasTarefasMapeadas.push({
                uid: tarefaExistente?.uid || `task_${Date.now()}_${count++}`,
                processoId: Number(procId),
                nome: `${procOriginal.nome} #${i}`,
                valorUnitario: Number(config.valorEditado || 0),
                concluida: tarefaExistente ? true : false,
                createdAt: tarefaExistente?.createdAt,
                concludedAt: tarefaExistente?.concludedAt
              });
            }
          }
        });

        const totalBonificacao = novasTarefasMapeadas.reduce((s, t) => s + Number(t.valorUnitario || 0), 0);

        const payload = {
          ...formTicket,
          contactId: formTicket.contactId ? Number(formTicket.contactId) : null,
          userId: formTicket.userId ? Number(formTicket.userId) : null,
          servicoId: Number(formTicket.servicoId),
          sistemaId: Number(formTicket.sistemaId),
          tarefas: novasTarefasMapeadas,
          bonificacao: totalBonificacao
        };

        const { data } = await api.put(`/tickets-custom/${formTicket.id}`, payload);
        setTickets(tickets.map(t => t.id === formTicket.id ? data : t));
        toast.success("Alterações salvas com sucesso!");
      } else {
        const tarefasGeradas = gerarTarefasUnicas(formTicket.processosQuantitativos, processosDisponiveis);
        const totalBonificacao = tarefasGeradas.reduce((s, t) => s + Number(t.valorUnitario || 0), 0);

        const payload = {
          ...formTicket,
          contactId: formTicket.contactId ? Number(formTicket.contactId) : null,
          userId: formTicket.userId ? Number(formTicket.userId) : null,
          servicoId: Number(formTicket.servicoId),
          sistemaId: Number(formTicket.sistemaId),
          tarefas: tarefasGeradas,
          bonificacao: totalBonificacao
        };

        const { data } = await api.post("/tickets-custom", payload);
        setTickets([data, ...tickets]);
        toast.success("Nova implantação criada com sucesso!");
      }
      setOpenModal(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Erro ao salvar o registro.");
    }
  };

  const handleToggleTarefaConcluida = async (taskUid, checked) => {
    if (!selectedTicket || (selectedTicket.status !== "pendente" && selectedTicket.status !== "em_andamento")) return;

    const tarefasAtualizadas = (selectedTicket.tarefas || []).map(t => {
      if (t.uid === taskUid) {
        return { ...t, concluida: checked, concludedAt: checked ? new Date() : null };
      }
      return t;
    });

    const payload = {
      ...selectedTicket,
      tarefas: tarefasAtualizadas
    };

    try {
      const { data } = await api.put(`/tickets-custom/${selectedTicket.id}`, payload);
      setTickets(tickets.map(t => t.id === selectedTicket.id ? data : t));
      setSelectedTicket(data);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar alteração da atividade.");
    }
  };

  const handleConfirmFeedbackAction = async () => {
    if (!selectedTicket) return;

    const payload = {
      ...selectedTicket,
      status: feedbackActionType,
      feedback: feedbackText
    };

    try {
      const { data } = await api.put(`/tickets-custom/${selectedTicket.id}`, payload);
      setTickets(tickets.map(t => t.id === selectedTicket.id ? data : t));
      setSelectedTicket(data);
      setOpenFeedbackModal(false);
      toast.success(`Operação finalizada como: ${feedbackActionType}`);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao alterar o status do ticket.");
    }
  };

  const handleOpenFeedback = (actionType) => {
    setFeedbackActionType(actionType);
    setFeedbackText("");
    setOpenFeedbackModal(true);
  };

  const handleOpenOperacional = (ticket) => {
    setSelectedTicket(ticket);
    setOpenOperacionalModal(true);
  };

  const handleDuplicateClick = async (ticket) => {
    const tarefasLimpas = (ticket.tarefas || []).map(t => ({
      uid: `task_${Date.now()}_${Math.random()}`,
      processoId: Number(t.processoId),
      nome: t.nome,
      valorUnitario: t.valorUnitario,
      concluida: false
    }));

    const payload = {
      contactId: ticket.contactId,
      userId: ticket.userId,
      servicoId: ticket.servicoId,
      sistemaId: ticket.sistemaId,
      nome: `${ticket.nome || ""} - Cópia`,
      descricao: ticket.descricao,
      status: "pendente",
      bonificacao: ticket.bonificacao,
      tarefas: tarefasLimpas
    };

    try {
      const { data } = await api.post("/tickets-custom", payload);
      setTickets([data, ...tickets]);
      toast.success(`Ticket #${ticket.id} duplicado com sucesso!`);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao duplicar o registro.");
    }
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Deseja deletar este ticket de implantação definitivamente?")) return;

    try {
      await api.delete(`/tickets-custom/${id}`);
      setTickets(tickets.filter(t => t.id !== id));
      if (selectedTicket && selectedTicket.id === id) setSelectedTicket(null);
      toast.success("Ticket removido.");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao deletar o registro.");
    }
  };

  const getUserName = (id) => {
    const user = users.find(u => Number(u.id) === Number(id));
    return user ? user.name : "Nenhum";
  };

  // 🟢 CORRIGIDO: Fallback dinâmico para não travar no "Carregando..."
  const getContactName = (contactId, ticketContact) => {
    if (ticketContact && ticketContact.name) return ticketContact.name;
    if (!contactId) return "Sem Contato";

    const contact = loadedContactsCache.find(c => String(c.id) === String(contactId));
    return contact ? contact.name : `Contato #${contactId}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "em_andamento": return classes.badgeAndamento;
      case "concluido": return classes.badgeConcluido;
      case "cancelado": return classes.badgeCancelado;
      default: return classes.badgePendente;
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesId = t.id ? t.id.toString().includes(filterId) : false;
    const matchesCliente = filterCliente === "" || String(t.contactId) === String(filterCliente);
    const matchesDescricao = (t.nome || "").toLowerCase().includes(filterDescricao.toLowerCase()) || (t.descricao || "").toLowerCase().includes(filterDescricao.toLowerCase());
    const matchesAnalista = filterAnalista === "" || Number(t.userId) === Number(filterAnalista);
    const matchesStatus = filterStatus === "" || t.status === filterStatus;

    return matchesId && matchesCliente && matchesDescricao && matchesAnalista && matchesStatus;
  });

  const totalGeral = selectedTicket ? Number(selectedTicket.bonificacao) : 0;
  const totalAprovadoAPagar = selectedTicket && selectedTicket.tarefas ? selectedTicket.tarefas.filter(t => t.concluida).reduce((s, t) => s + Number(t.valorUnitario), 0) : 0;
  const totalRestante = totalGeral - totalAprovadoAPagar;
  const todosConcluidos = selectedTicket && selectedTicket.tarefas ? selectedTicket.tarefas.every(t => t.concluida) : false;

  return (
    <div className={classes.mainContainer}>
      <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: 20 }}>
        <Typography variant="h5" color="primary" style={{ fontWeight: "bold" }}>
          Gestão de Implantações & Comissões
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>
          Abrir Implantação
        </Button>
      </Grid>

      {/* FILTROS */}
      <Paper className={classes.filterPaper} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={1}>
            <TextField label="ID" variant="outlined" fullWidth size="small" value={filterId} onChange={e => setFilterId(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={contactsOptions}
              loading={loadingContacts}
              getOptionLabel={(option) => option ? option.name || "" : ""}
              value={
                contactsOptions.find(c => String(c.id) === String(formTicket.contactId)) ||
                loadedContactsCache.find(c => String(c.id) === String(formTicket.contactId)) ||
                null
              }
              onChange={(event, newValue) => setFilterCliente(newValue ? newValue.id : "")}
              onInputChange={(e, value) => setSearchParam(value)}
              getOptionSelected={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente / Contato"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField label="Pesquisar Título/Obs" variant="outlined" fullWidth size="small" value={filterDescricao} onChange={e => setFilterDescricao(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Filtrar por Analista" variant="outlined" fullWidth size="small" value={filterAnalista} onChange={e => setFilterAnalista(e.target.value)}>
              <MenuItem value="">Todos os Analistas</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField select label="Status" variant="outlined" fullWidth size="small" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos os Status</MenuItem>
              <MenuItem value="pendente">Pendente</MenuItem>
              <MenuItem value="em_andamento">Em Andamento</MenuItem>
              <MenuItem value="concluido">Concluído</MenuItem>
              <MenuItem value="cancelado">Cancelado</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* TABELA PRINCIPAL COMPLETA */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="medium">
              <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>ID</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Cliente / Escopo</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Analista</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell style={{ fontWeight: "bold", textAlign: "center" }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">Buscando dados no servidor...</TableCell>
                  </TableRow>
                ) : filteredTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">Nenhum registro encontrado.</TableCell>
                  </TableRow>
                ) : (
                  filteredTickets.map(ticket => (
                    <TableRow key={ticket.id} className={classes.tableRow}>
                      <TableCell style={{ fontWeight: "bold" }}>{ticket.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {getContactName(ticket.contactId, ticket.contact)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {ticket.nome ? `${ticket.nome} - ` : ""}{(ticket.descricao || "").substring(0, 60)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{getUserName(ticket.userId)}</TableCell>
                      <TableCell>
                        <Chip label={ticket.status.toUpperCase().replace("_", " ")} size="small" className={getStatusBadge(ticket.status)} />
                      </TableCell>
                      <TableCell align="center">
                        <div className={classes.actionButtons} style={{ justifyContent: "center" }}>
                          <IconButton size="small" color="default" title="Duplicar Operação" onClick={() => handleDuplicateClick(ticket)}>
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="primary" title="Editar Registro/Status" onClick={() => handleOpenEditModal(ticket)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" style={{ color: "#2e7d32" }} title="Painel Operacional / Checklist" onClick={() => handleOpenOperacional(ticket)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="secondary" title="Excluir" onClick={() => handleDeleteClick(ticket.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* TELA OPERACIONAL E CHECKLIST */}
      <Dialog open={openOperacionalModal} onClose={() => setOpenOperacionalModal(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ fontWeight: "bold" }}>
          {selectedTicket && `Painel Operacional & Checklist da Implantação #${selectedTicket.id}`}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Cliente / Operação</Typography>
                <Typography variant="body1" style={{ fontWeight: 600, color: "#1976d2" }}>
                  {getContactName(selectedTicket.contactId, selectedTicket.contact)}
                </Typography>
                {selectedTicket.nome && (
                  <Typography variant="body2" style={{ fontWeight: 500, marginTop: 4 }}>
                    Projeto: {selectedTicket.nome}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                  Criado em: {selectedTicket.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : ""} {selectedTicket.concludedAt && ` | Concluído em: ${new Date(selectedTicket.concludedAt).toLocaleString()}`}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Responsável Técnico</Typography>
                <Typography variant="body2">{getUserName(selectedTicket.userId)}</Typography>
                <div style={{ marginTop: 8 }}>
                  <Chip label={selectedTicket.status.toUpperCase().replace("_", " ")} className={getStatusBadge(selectedTicket.status)} />
                </div>
              </Grid>

              <Grid item xs={12}>
                <Divider style={{ margin: "8px 0" }} />
                <Typography variant="body2" color="textSecondary" style={{ fontWeight: "bold", marginBottom: 6 }}>
                  Checklist das Atividades (Clique nas caixas para computar a conclusão):
                </Typography>
                <div className={classes.checklistContainer}>
                  {selectedTicket.tarefas && selectedTicket.tarefas.map(t => (
                    <div key={t.uid} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed #eee" }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <FormControlLabel
                          style={{ marginBottom: 0 }}
                          control={
                            <Checkbox
                              color="primary"
                              checked={!!t.concluida}
                              disabled={selectedTicket.status !== "pendente" && selectedTicket.status !== "em_andamento"}
                              onChange={(e) => handleToggleTarefaConcluida(t.uid, e.target.checked)}
                            />
                          }
                          label={<span style={{ textDecoration: t.concluida ? "line-through" : "none", fontSize: "0.9rem" }}>{t.nome}</span>}
                        />
                        <Typography variant="caption" color="textSecondary" style={{ marginLeft: 30, fontSize: "0.75rem" }}>
                          Abertura: {t.createdAt ? new Date(t.createdAt).toLocaleString() : ""} {t.concludedAt && ` | Finalizado: ${new Date(t.concludedAt).toLocaleString()}`}
                        </Typography>
                      </div>
                      <Typography variant="body2" style={{ fontWeight: "bold" }}>
                        R$ {Number(t.valorUnitario).toFixed(2)}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Concluído (Valor a Receber)</Typography>
                <Typography variant="h6" style={{ color: "#2e7d32", fontWeight: "bold" }}>
                  {totalAprovadoAPagar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Restante Pendente</Typography>
                <Typography variant="h6" style={{ color: "#c62828", fontWeight: "bold" }}>
                  {totalRestante.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Grid>

              {selectedTicket.feedback && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Feedback Final</Typography>
                  <Paper style={{ padding: 12, backgroundColor: "#f9f9f9", fontStyle: "italic" }} variant="outlined">
                    <Typography variant="body2">"{selectedTicket.feedback}"</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          {selectedTicket && (selectedTicket.status === "pendente" || selectedTicket.status === "em_andamento") && (
            <div style={{ display: "flex", gap: 10, width: "100%", justifyContent: "flex-start" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                disabled={!todosConcluidos}
                onClick={() => handleOpenFeedback("concluido")}
              >
                Finalizar Entrega
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={() => handleOpenFeedback("cancelado")}
              >
                Cancelar Operação
              </Button>
            </div>
          )}
          <Button onClick={() => setOpenOperacionalModal(false)} variant="contained" color="default">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG DE CADASTRO/EDIÇÃO */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSaveTicket}>
          <DialogTitle style={{ fontWeight: "bold" }}>
            {isEditing ? `Alterar Implantação #${formTicket.id}` : "Nova Implantação de Cliente"}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Status do Ticket"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.status}
                  onChange={e => setFormTicket({ ...formTicket, status: e.target.value })}
                >
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="em_andamento">Em Andamento</MenuItem>
                  <MenuItem value="concluido">Concluído</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  select
                  label="Analista Técnico"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.userId}
                  onChange={e => setFormTicket({ ...formTicket, userId: Number(e.target.value) })}
                >
                  {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={contactsOptions}
                  loading={loadingContacts}
                  getOptionLabel={(option) => option ? option.name || "" : ""}
                  value={loadedContactsCache.find(c => String(c.id) === String(formTicket.contactId)) || null}
                  onChange={(event, newValue) => {
                    setFormTicket({ ...formTicket, contactId: newValue ? newValue.id : "" });
                  }}
                  onInputChange={(e, value) => setSearchParam(value)}
                  getOptionSelected={(option, value) => String(option.id) === String(value.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente / Contato"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Nome do Projeto / Escopo Geral (Opcional)"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formTicket.nome}
                  onChange={e => setFormTicket({ ...formTicket, nome: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  select
                  label="Tipo de Serviço"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.servicoId}
                  onChange={e => handleServicoChange(e.target.value)}
                >
                  {estruturaServicos.map(s => <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  select
                  label="Sistema"
                  variant="outlined"
                  fullWidth
                  required
                  disabled={sistemasDisponiveis.length === 0}
                  size="small"
                  value={formTicket.sistemaId}
                  onChange={e => handleSistemaChange(e.target.value)}
                >
                  {sistemasDisponiveis.map(sys => <MenuItem key={sys.id} value={sys.id}>{sys.nome}</MenuItem>)}
                </TextField>
              </Grid>

              {processosDisponiveis.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" style={{ fontWeight: "bold", color: "#555", marginBottom: 5 }}>
                    Informe a Qtd e Ajuste o Valor de cada item se necessário:
                  </Typography>
                  <div className={classes.processBox}>
                    {processosDisponiveis.map(proc => {
                      const dadosItem = formTicket.processosQuantitativos[proc.id] || { qtd: 0, valorEditado: proc.valorUnitario };
                      return (
                        <div key={proc.id} className={classes.processRow}>
                          <Typography variant="body2" style={{ width: "45%" }}>{proc.nome}</Typography>

                          <TextField
                            label="Qtd"
                            type="number"
                            size="small"
                            variant="outlined"
                            style={{ width: 70 }}
                            value={dadosItem.qtd}
                            onChange={e => handleProcessoValoresChange(proc.id, "qtd", e.target.value)}
                          />

                          <TextField
                            label="Valor Unitário (R$)"
                            type="number"
                            size="small"
                            variant="outlined"
                            style={{ width: 130 }}
                            value={dadosItem.valorEditado}
                            onChange={e => handleProcessoValoresChange(proc.id, "valorEditado", e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="Notas de Escopo da Implantação"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  required
                  value={formTicket.descricao}
                  onChange={e => setFormTicket({ ...formTicket, descricao: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Valor da Bonificação Total (R$)"
                  variant="outlined"
                  fullWidth
                  disabled
                  size="small"
                  value={Number(formTicket.bonificacao).toFixed(2)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)} color="secondary">Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">Confirmar</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DIALOG DE FEEDBACK */}
      <Dialog open={openFeedbackModal} onClose={() => setOpenFeedbackModal(false)} maxWidth="xs" fullWidth style={{ zIndex: 1600 }}>
        <DialogTitle style={{ fontWeight: "bold" }}>
          {feedbackActionType === "concluido" ? "Finalizar Implantação" : "Cancelar Operação"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 15 }}>
            {feedbackActionType === "concluido"
              ? "Para concluir, informe o feedback que o cliente deu sobre a entrega técnica:"
              : "Informe o motivo do cancelamento deste registro:"}
          </Typography>
          <TextField
            label="Feedback / Observações"
            variant="outlined"
            fullWidth
            required
            multiline
            rows={4}
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFeedbackModal(false)}>Voltar</Button>
          <Button
            onClick={handleConfirmFeedbackAction}
            variant="contained"
            color={feedbackActionType === "concluido" ? "primary" : "secondary"}
            disabled={!feedbackText.trim()}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}